import React, { useEffect, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem,
  Alert, CircularProgress, Typography, Box,
} from '@mui/material';

// Asking for a private lesson: which coach, and when. Buying a private only
// adds a session to the balance; an admin books the lesson on the schedule
// once this request arrives (requestPrivateSession emails the club).
export default function PrivateSessionRequestDialog({ open, onClose, athleteId }) {
  const [options, setOptions] = useState(null);
  const [coachId, setCoachId] = useState('');
  const [times, setTimes] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(null);

  useEffect(() => {
    if (!open || !athleteId) return;
    setError(''); setSent(null);
    httpsCallable(getFunctions(), 'getPrivateRequestOptions')({ athleteId })
      .then(({ data }) => setOptions(data))
      .catch(e => setError(e.message || 'Could not load the coaches.'));
  }, [open, athleteId]);

  const submit = async () => {
    setBusy(true); setError('');
    try {
      const { data } = await httpsCallable(getFunctions(), 'requestPrivateSession')({
        athleteId, coachId, times, notes,
      });
      setSent(data);
      setTimes(''); setNotes(''); setCoachId('');
    } catch (e) {
      setError(e.message || 'The request did not go through. Please try again.');
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle>
        Book a private lesson{options?.athleteName ? ` for ${options.athleteName}` : ''}
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {sent ? (
          <Alert severity="success">
            Request sent{sent.coachName && sent.coachName !== 'No preference' ? ` for ${sent.coachName}` : ''}.
            We'll be in touch to confirm the time, and the lesson will appear on the schedule
            once it's booked.
          </Alert>
        ) : !options && !error ? (
          <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={28} /></Box>
        ) : options ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography variant="body2" sx={{ color: '#555' }}>
              Tell us who you'd like to work with and when you're free. We'll set it up and confirm.
            </Typography>
            <TextField select label="Preferred coach" value={coachId}
              onChange={e => setCoachId(e.target.value)}>
              <MenuItem value="">No preference</MenuItem>
              {options.coaches.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </TextField>
            <TextField label="Days and times that work" required multiline minRows={2}
              placeholder="e.g. Tue or Thu after 4 PM, Saturday mornings"
              value={times} onChange={e => setTimes(e.target.value)} />
            <TextField label="Anything to work on? (optional)" multiline minRows={2}
              value={notes} onChange={e => setNotes(e.target.value)} />
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{sent ? 'Done' : 'Cancel'}</Button>
        {!sent && options && (
          <Button variant="contained" onClick={submit} disabled={busy || !times.trim()}>
            {busy ? 'Sending…' : 'Send request'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
